import { Helmet } from 'react-helmet';

import { PageHeader } from 'shared-components';

import { hashToArray } from '../../../../utilities/helpers';
import { sortFn } from '../../../../utilities/documents';
import { InterviewItem } from './interviewItem';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import MyInterviewItem from './myInterviewItem';
import { IRecordedInterview, RecordedInterviewSystemStatus } from 'types';
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import PageSubtext from '../../../atoms/PageSubtext';
import SectionHeader from '../../../atoms/SectionHeader';
import { titleAppend } from '../../../../utilities/head';
import { getStudentRecordedInterviews } from '../../../../store/student/actions';
import { getStudentDocs } from '../../../../store/student/actions';
import { useSession } from 'shared-components/authentication';


export const ScheduledInterviews = () => {
  

  const calendlyInterviews = useSelector((state: ApplicationState) => state.student.offer.calendlyInterviews) || [];
  const personalInfo = useSelector((state: ApplicationState) => state.student.personalInfo);
  const core = useSelector((state: ApplicationState) => state.student.core);
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const jobId = useSelector((state: ApplicationState) => state.config?.recordedInterview?.jobId ?? 'unknownJobId');

  const dispatch = useDispatch();

  
  const onGetRecordedInterviews = () => {
    dispatch(getStudentRecordedInterviews());
  };
  const [idToken, setIdToken] = useState('');
  const [uni, studentNo] = core.student_id.split('#');

  const sessionContext = useSession();

  useEffect(() => {
    const getIdToken = async () => {
      const idToken = await sessionContext.getIdToken();
      // console.log('got id token from session', idToken);
      setIdToken(idToken);
    };
    getIdToken();
  }, []);

  useEffect(() => {
    dispatch(getStudentDocs());
  }, [dispatch]);

  useEffect(() => {
    onGetRecordedInterviews();
  }, []);

  const recordedInterviews = useSelector(
    (state: ApplicationState) => state.student.offer.recordedInterviews || null,
    (oldValue, newValue) => {
      const filteredOldValue =
        oldValue &&
        Object.values(oldValue).map(interview => {
          const { attempts, questions, ...rest } = interview;
          return rest;
        });
      const filteredNewValue =
        newValue &&
        Object.values(newValue).map(interview => {
          const { attempts, questions, ...rest } = interview;
          return rest;
        });
      return shallowEqual(filteredOldValue, filteredNewValue);
    }
  );

  const myInterviewList = useMemo(() => {
    if (_.isEmpty(recordedInterviews)) {
      return <></>;
    }
    const sortedRecordedInterviews = Object.entries(recordedInterviews as IRecordedInterview)
      .filter(
        ([_, interview]) => interview.systemStatus !== RecordedInterviewSystemStatus.DELETED && !interview.deletedByTTL
      )
      .map(item => {
        const [interviewHash, interview] = item;
        return {
          ...interview,
          interviewHash
        };
      })
      .sort((a, b) => (a.hashTimestamp ?? 1) - (b.hashTimestamp ?? 0));

    return sortedRecordedInterviews && idToken ? (
      <>
        <SectionHeader
          title="Complete your recorded video interview"
          subtext="You need to complete a short video recording of yourself answering some questions. You can do this at a time
          that is convenient for you, but please ensure that you have a good internet connection, and a working camera
          and microphone. Please have your passport ready before starting."
        />

        {sortedRecordedInterviews.map(({ interviewHash, ...interviewEntry }) => (
          <MyInterviewItem
            uni={uni}
            studentNo={studentNo}
            key={interviewHash}
            interviewHash={interviewHash}
            interview={interviewEntry}
            jobId={jobId}
            interviewAttemptsCount={Object.keys(interviewEntry.attempts || {}).length}
            isExceeded={interviewEntry.systemStatus === RecordedInterviewSystemStatus.EXCEEDED}
            idToken={idToken}
          />
        ))}
      </>
    ) : (
      <></>
    );
  }, [recordedInterviews, idToken, uni, studentNo, jobId]);

  const pageTitle = 'Interviews';

  return (
    <>
      <Helmet>
        <title>
          {pageTitle} {titleAppend}
        </title>
      </Helmet>

      <PageHeader>{pageTitle}</PageHeader>

      <PageSubtext>
        You have been invited to complete the following interview
        {recordedInterviews || calendlyInterviews.length > 1 ? 's' : null}. Please follow the instructions below.
      </PageSubtext>

      <div data-selector="ScheduledInterviews-container">
        {myInterviewList}

        {!!calendlyInterviews.length && (
          <>
            <SectionHeader
              title="Book an interview with our team"
              subtext="You need to attend an interview with a member of our team. Please schedule a time for your interview, and
            you will then receive further details."
            />

            {calendlyInterviews.map(interview => {
              const eventArr = hashToArray(interview.scheduledEvents ?? {}).sort(sortFn);

              const firstName = `${personalInfo?.givenName ?? importData?.givenName ?? ''} ${
                personalInfo?.middleName ?? ''
              }`.trim();
              const lastName = personalInfo?.surname ?? importData?.familyName ?? '';
              const studentDetails = {
                name: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
                email: core?.email ?? '',
                firstName,
                lastName
              };

              return eventArr.map(obj => {
                const { key, item } = obj;
                return <InterviewItem key={key} interview={interview} event={item} studentDetails={studentDetails} />;
              });
            })}
          </>
        )}
      </div>
    </>
  );
};

export default ScheduledInterviews;
